import './visualEditProps';
import React, { useEffect, useRef } from 'react';
import { Canvas, advance, useFrame, useThree } from '@react-three/fiber';
import { Grid, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { ClusterId, HeadId, StageId, Vec3 } from '../data/content';
import type { WordInfo } from '../lib/model';
import { cameraFor } from '../lib/layout';
import { TokenObject, type SceneToken } from './TokenObject';
import { BlankTile } from './BlankTile';
import { MeaningCloud } from './MeaningCloud';
import { AttentionArcs } from './AttentionArcs';
import { LayerStack } from './LayerStack';
import { PredictionBars } from './PredictionBars';
type Props = {
  stage: StageId;
  tokens: SceneToken[];
  widths: number[];
  tightXs: number[];
  spreadXs: number[];
  rowWidth: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
  head: HeadId;
  attention: number[];
  hiddenWords: Set<string>;
  neighbors: WordInfo[];
  focusCluster: ClusterId | null;
  candidates: {
    text: string;
    prob: number;
  }[];
  picking: number | null;
  viewKey: number;
  wide: boolean;
  reducedMotion: boolean;
  /** The film is playing: sway the camera so each shot feels filmed. */
  drift: boolean;
  /** The viewer started orbiting. */
  onUserInteract: () => void;
  /** The explore panel is covering the right side of the screen. */
  panelOpen: boolean;
  blank: {
    x: number;
    visible: boolean;
  };
  framing: 'wide' | null;
  onContextLost: () => void;
};
export function Scene(props: Props) {
  const {
    stage,
    tokens,
    widths,
    tightXs,
    spreadXs,
    rowWidth,
    selectedIndex,
    onSelect,
    head,
    attention,
    hiddenWords,
    neighbors,
    focusCluster,
    candidates,
    picking,
    viewKey,
    wide,
    reducedMotion,
    drift,
    onUserInteract,
    panelOpen,
    blank,
    framing: shotFraming,
    onContextLost
  } = props;
  const framing = cameraFor(stage, rowWidth, shotFraming);
  const initialCamera = useRef(framing.pos).current;
  const last = tokens.length - 1;
  const selected = tokens[selectedIndex];
  return <Canvas flat dpr={[1, 1.5]} gl={{
    antialias: true,
    alpha: true
  }} camera={{
    position: initialCamera,
    fov: 42,
    near: 0.1,
    far: 200
  }} data-id="element-70" data-mp="3ahy7i9th0gfo">
      <ContextGuard onLost={onContextLost} data-id="element-71" data-mp="31ib81tkaxkp9" />
      <fogExp2 attach="fog" args={['#090A10', 0.03]} data-id="element-72" data-mp="2wkhyqrnpcakr" />
      <ambientLight intensity={0.35} data-id="element-73" data-mp="2wjzv39ypmu2t" />
      <hemisphereLight args={['#9FB0FF', '#07080C', 0.3]} data-id="element-74" data-mp="2rlesx4zwe0bq" />
      <directionalLight position={[6, 12, 8]} intensity={1.6} color="#FFE9CF" data-id="element-75" data-mp="2rmzp0z1v1rbp" />
      <directionalLight position={[-8, 6, -10]} intensity={0.8} color="#8EA6FF" data-id="element-76" data-mp="2mtnchnaghazb" />

      <Grid args={[80, 80]} cellSize={0.5} cellThickness={0.6} cellColor="#161925" sectionSize={2.5} sectionThickness={0.8} sectionColor="#1F2333" fadeDistance={26} fadeStrength={2} infiniteGrid data-id="element-77" data-mp="2islbp20w4qv8" />

      {tokens.map((token, i) => <TokenObject key={token.key} index={i} token={token} tightX={tightXs[i]} spreadX={spreadXs[i]} width={widths[i]} stage={stage} selected={i === selectedIndex} dimmed={stage === 'attention' && i > selectedIndex} isLast={i === last} onSelect={onSelect} reducedMotion={reducedMotion} data-id="element-78" data-mp="23wvo4zg245oe" data-mpi={i} />)}

      <BlankTile x={blank.x} visible={blank.visible} reducedMotion={reducedMotion} data-id="element-79" data-mp="28oeltw8ghgx4" />

      {stage === 'meaning' && <MeaningCloud hiddenWords={hiddenWords} neighbors={neighbors} focus={selected ? {
      pos: selected.cloudPos,
      color: selected.color
    } : null} focusCluster={focusCluster} reducedMotion={reducedMotion} data-id="element-80" data-mp="1azl1hzf3y2rg" />}

      {stage === 'attention' && <AttentionArcs xs={spreadXs} colors={tokens.map(t => t.color)} selectedIndex={selectedIndex} weights={attention} head={head} reducedMotion={reducedMotion} data-id="element-81" data-mp="saxwti0uffee" />}

      <LayerStack width={rowWidth} stage={stage} reducedMotion={reducedMotion} data-id="element-82" data-mp="2yzyk2fes7d1a" />

      {stage === 'prediction' && <PredictionBars candidates={candidates} picking={picking} lastX={spreadXs[last]} reducedMotion={reducedMotion} data-id="element-83" data-mp="2u1x1rwp6jhqv" />}

      {/* Not JSX, so the platform's visual-edit props aren't added: these controls
          come from three-stdlib and don't get the `data` bag in visualEditProps. */}
      {React.createElement(OrbitControls, {
      makeDefault: true,
      enableDamping: true,
      dampingFactor: 0.08,
      minDistance: 4,
      maxDistance: 40,
      maxPolarAngle: Math.PI * 0.53,
      autoRotateSpeed: 0.35
    })}
      <CameraRig animKey={`${stage}-${shotFraming}-${viewKey}-${tokens.length}`} pos={framing.pos} target={framing.target} reducedMotion={reducedMotion} autoRotate={stage === 'meaning' && !reducedMotion} drift={drift && !reducedMotion} onUserInteract={onUserInteract} data-id="element-84" data-mp="3mpkfqsvy3a4j" />
      <ViewOffset wide={wide} shiftX={wide && panelOpen ? 170 : 0} data-id="element-85" data-mp="crsm0mgyukya" />
      <FrameKeeper data-id="element-86" data-mp="cr0w9sa1vhfx" />
    </Canvas>;
}
type ControlsLike = {
  target: THREE.Vector3;
  enabled: boolean;
  autoRotate: boolean;
  update: () => void;
  addEventListener: (type: 'start', listener: () => void) => void;
  removeEventListener: (type: 'start', listener: () => void) => void;
};
const UP = new THREE.Vector3(0, 1, 0);
const driftOffset = new THREE.Vector3();

/** Eases the camera to each stage's framing, then hands control back to the viewer. */
function CameraRig({
  animKey,
  pos,
  target,
  reducedMotion,
  autoRotate,
  drift,
  onUserInteract
}: {
  animKey: string;
  pos: Vec3;
  target: Vec3;
  reducedMotion: boolean;
  autoRotate: boolean;
  drift: boolean;
  onUserInteract: () => void;
}) {
  const aspect = useThree(s => s.size.width / Math.max(1, s.size.height));
  const controls = useThree(s => s.controls) as unknown as ControlsLike | null;
  // Framings are tuned for landscape screens; back away on narrow ones.
  const fit = Math.min(2.6, Math.max(1, 1.45 / aspect));
  const goal = useRef({
    pos,
    target,
    fit
  });
  goal.current = {
    pos,
    target,
    fit
  };
  const interact = useRef(onUserInteract);
  interact.current = onUserInteract;
  const anim = useRef({
    pending: true,
    active: false,
    start: 0,
    settledAt: 0,
    fromPos: new THREE.Vector3(),
    fromTarget: new THREE.Vector3(),
    toPos: new THREE.Vector3(),
    toTarget: new THREE.Vector3()
  });
  const fitBucket = Math.round(fit * 10);
  useEffect(() => {
    anim.current.pending = true;
  }, [animKey, fitBucket]);

  // Only fires for the viewer's own drags, never for the moves made here.
  useEffect(() => {
    if (!controls) return;
    const onStart = () => interact.current();
    controls.addEventListener('start', onStart);
    return () => controls.removeEventListener('start', onStart);
  }, [controls]);
  useFrame(state => {
    const controls = state.controls as unknown as ControlsLike | null;
    if (!controls) return;
    const a = anim.current;
    const now = state.clock.elapsedTime;
    if (a.pending) {
      a.pending = false;
      a.active = true;
      a.start = now;
      a.fromPos.copy(state.camera.position);
      a.fromTarget.copy(controls.target);
      a.toTarget.set(...goal.current.target);
      a.toPos.set(...goal.current.pos).sub(a.toTarget).multiplyScalar(goal.current.fit).add(a.toTarget);
      controls.enabled = false;
    }
    if (a.active) {
      const duration = reducedMotion ? 0.05 : 1.4;
      const k = Math.min(1, (now - a.start) / duration);
      const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      state.camera.position.lerpVectors(a.fromPos, a.toPos, e);
      controls.target.lerpVectors(a.fromTarget, a.toTarget, e);
      state.camera.lookAt(controls.target);
      if (k >= 1) {
        a.active = false;
        a.settledAt = now;
        controls.enabled = true;
        controls.update();
      }
    } else if (drift) {
      const t = now - a.settledAt;
      driftOffset.copy(a.toPos).sub(a.toTarget).applyAxisAngle(UP, 0.07 * Math.sin(t * 0.18)).multiplyScalar(1 - 0.035 * Math.sin(t * 0.11));
      state.camera.position.copy(a.toTarget).add(driftOffset);
      controls.target.copy(a.toTarget);
      state.camera.lookAt(a.toTarget);
    }
    controls.autoRotate = autoRotate && !a.active && !drift;
  });
  return null;
}
const RESTORE_WAIT_MS = 1500;

/**
 * three.js asks the browser to restore a lost context and picks back up if it
 * does. Only report the loss when that hasn't happened in time.
 */
function ContextGuard({
  onLost
}: {
  onLost: () => void;
}) {
  const gl = useThree(s => s.gl);
  useEffect(() => {
    const canvas = gl.domElement;
    let timer = 0;
    const lost = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(onLost, RESTORE_WAIT_MS);
    };
    const restored = () => window.clearTimeout(timer);
    canvas.addEventListener('webglcontextlost', lost);
    canvas.addEventListener('webglcontextrestored', restored);
    return () => {
      window.clearTimeout(timer);
      canvas.removeEventListener('webglcontextlost', lost);
      canvas.removeEventListener('webglcontextrestored', restored);
    };
  }, [gl, onLost]);
  return null;
}
const KEEPALIVE_MS = 4000;

/**
 * Canvas thumbnails freeze `requestAnimationFrame` shortly after load, which
 * can land before the first WebGL frame and leave the scene blank. While frames
 * stop arriving, drive them from a timer long enough for the scene to settle
 * into a still.
 */
function FrameKeeper() {
  useEffect(() => {
    const start = performance.now();
    let lastRaf = start;
    const id = window.setInterval(() => {
      const now = performance.now();
      if (now - start > KEEPALIVE_MS) {
        window.clearInterval(id);
        return;
      }
      window.requestAnimationFrame(() => {
        lastRaf = performance.now();
      });
      if (now - lastRaf > 150) advance(now);
    }, 33);
    return () => window.clearInterval(id);
  }, []);
  return null;
}

/**
 * Lifts the subject clear of the captions along the bottom, and slides it left
 * while the explore panel covers the right.
 */
function ViewOffset({
  wide,
  shiftX
}: {
  wide: boolean;
  shiftX: number;
}) {
  const x = useRef(shiftX);
  useFrame((state, delta) => {
    const camera = state.camera;
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    const {
      width,
      height
    } = state.size;
    x.current = THREE.MathUtils.damp(x.current, shiftX, 6, Math.min(delta, 0.05));
    const y = wide ? 44 : height * 0.1;
    const view = camera.view;
    if (view?.enabled && view.fullWidth === width && view.fullHeight === height && Math.abs(view.offsetX - x.current) < 0.1 && view.offsetY === y) {
      return;
    }
    camera.setViewOffset(width, height, x.current, y, width, height);
    camera.updateProjectionMatrix();
  });
  return null;
}